import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Card, PageStatus } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import { useStoreId } from '~/providers/StoreProvider';
import { roleCodeLabel } from '~/utils/roleCodeLabel';
import { StorePage } from './StorePage';

type StoreUser = CommandAdminApi.ResponseBody<
  '/store/:storeId/user',
  'get'
>[number];

// per-grant rows have no natural single id; index keeps DataTable keys unique
type StoreUserRow = StoreUser & { id: number };

const { idColumn, createdOnColumn } = sharedTableDefinitions;

const fullName = (user: StoreUser) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ') || '—';

const userColumns: ColumnDef<StoreUserRow>[] = [
  idColumn<StoreUserRow>({
    accessorKey: 'userId',
    header: 'ID',
    meta: { align: 'left' },
  }),
  { id: 'name', header: 'Name', accessorFn: fullName, enableSorting: true },
  { accessorKey: 'email', header: 'Email', enableSorting: true },
  {
    accessorKey: 'roleCode',
    header: 'Role',
    enableSorting: true,
    cell: ({ row }) => <Badge>{roleCodeLabel[row.original.roleCode]}</Badge>,
  },
  createdOnColumn<StoreUserRow>(),
];

export default function StoreUsersPage() {
  const storeId = useStoreId();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['store', storeId, 'users'],
    queryFn: () => api.store(storeId).listUsers(),
  });

  return (
    <StorePage title="Store users">
      {match(query)
        .with({ isPending: true }, () => (
          <PageStatus.Loading title="Loading users" />
        ))
        .with({ isError: true }, () => (
          <PageStatus.Error title="Failed to load users" error={query.error} />
        ))
        .with({ isSuccess: true, data: P.select() }, (users) => {
          if (users.length === 0) {
            return (
              <DataTableStatus
                icon="accounts"
                title="No users with access"
                details="Users granted a direct store role will appear here."
              />
            );
          }

          const rows = users.map((user, index) => ({ ...user, id: index }));

          return (
            <Card>
              <DataTable
                data={rows}
                columns={userColumns}
                pageSize={50}
                filterable
                onRowClick={(row) =>
                  navigate({
                    pathname: `/users/${row.userId}`,
                    search: new URLSearchParams({
                      returnTo: `/stores/${storeId}/users`,
                    }).toString(),
                  })
                }
              />
            </Card>
          );
        })
        .exhaustive()}
    </StorePage>
  );
}
